// src/components/Layout.js — Persistent shell: sidebar + main content area
import { useState, useEffect } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'

const SIDEBAR_COLLAPSED_KEY = 'rga_sidebar_collapsed'
const SIDEBAR_FULL_W  = 232
const SIDEBAR_MINI_W  = 52

// ── Inline SVG icons ──────────────────────────────────────────────────────
const Icons = {
  dashboard: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1"/>
      <rect x="14" y="3" width="7" height="7" rx="1"/>
      <rect x="3" y="14" width="7" height="7" rx="1"/>
      <rect x="14" y="14" width="7" height="7" rx="1"/>
    </svg>
  ),
  pipeline: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="4" height="18" rx="1"/>
      <rect x="10" y="8" width="4" height="13" rx="1"/>
      <rect x="17" y="5" width="4" height="16" rx="1"/>
    </svg>
  ),
  contacts: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  firms: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
    </svg>
  ),
  partners: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
    </svg>
  ),
  interactions: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
      <polyline points="22,6 12,13 2,6"/>
    </svg>
  ),
  settings: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  ),
  hamburger: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="6" x2="21" y2="6"/>
      <line x1="3" y1="12" x2="21" y2="12"/>
      <line x1="3" y1="18" x2="21" y2="18"/>
    </svg>
  ),
  close: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  chevronLeft: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6"/>
    </svg>
  ),
  chevronRight: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6"/>
    </svg>
  ),
}

// ── Nav item ──────────────────────────────────────────────────────────────
function NavItem({ to, icon, label, end, collapsed, onClick }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      title={collapsed ? label : undefined}
      style={({ isActive }) => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        gap: '10px',
        padding: collapsed ? '10px 0' : '8px 12px',
        borderRadius: '6px',
        fontSize: '13px',
        fontWeight: isActive ? '600' : '400',
        color: isActive ? '#f9fafb' : '#9ca3af',
        background: isActive ? 'rgba(59, 130, 246, 0.12)' : 'transparent',
        transition: 'background 0.12s, color 0.12s',
        userSelect: 'none',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      })}
      onMouseEnter={e => {
        if (!e.currentTarget.classList.contains('active')) {
          e.currentTarget.style.background = 'rgba(255,255,255,0.04)'
          e.currentTarget.style.color = '#e5e7eb'
        }
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = ''
        e.currentTarget.style.color = ''
      }}
    >
      <span style={{ opacity: 0.8, flexShrink: 0 }}>{icon}</span>
      {!collapsed && label}
    </NavLink>
  )
}

// ── Wordmark ──────────────────────────────────────────────────────────────
function Wordmark({ collapsed }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
      <div style={{
        width: '28px', height: '28px',
        background: '#3b82f6', borderRadius: '6px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
        </svg>
      </div>
      {!collapsed && (
        <div style={{ overflow: 'hidden' }}>
          <div style={{ fontSize: '13px', fontWeight: '700', color: '#f9fafb', letterSpacing: '0.01em', whiteSpace: 'nowrap' }}>
            RGA CRM
          </div>
          <div style={{ fontSize: '10px', color: '#4b5563', fontWeight: '500', letterSpacing: '0.04em', textTransform: 'uppercase', marginTop: '1px', whiteSpace: 'nowrap' }}>
            Rainmaker Global
          </div>
        </div>
      )}
    </div>
  )
}

// ── Layout ────────────────────────────────────────────────────────────────
export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false)   // mobile slide-in
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true' }
    catch { return false }
  })
  const location = useLocation()
  const { user, signOut } = useAuth()

  useEffect(() => { setSidebarOpen(false) }, [location.pathname])

  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [sidebarOpen])

  function toggleCollapsed() {
    setCollapsed(c => {
      const next = !c
      try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next)) } catch {}
      return next
    })
  }

  const closeNav = () => setSidebarOpen(false)
  const sidebarW = collapsed ? SIDEBAR_MINI_W : SIDEBAR_FULL_W

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>

      {/* ── Mobile top bar ───────────────────────────────────────────── */}
      <div className="mobile-topbar">
        <button
          onClick={() => setSidebarOpen(true)}
          style={{
            background: 'transparent', border: 'none', color: '#9ca3af',
            cursor: 'pointer', padding: '6px', borderRadius: '6px',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          {Icons.hamburger}
        </button>
        <Wordmark collapsed={false} />
      </div>

      {/* ── Mobile backdrop ──────────────────────────────────────────── */}
      {sidebarOpen && <div className="sidebar-backdrop" onClick={closeNav} />}

      {/* ── Sidebar ──────────────────────────────────────────────────── */}
      <aside
        className={`sidebar-nav${sidebarOpen ? ' sidebar-open' : ''}`}
        style={{
          width: `${sidebarW}px`,
          flexShrink: 0,
          background: '#0d1321',
          borderRight: '1px solid #1f2937',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
          transition: 'width 0.25s ease',
          position: 'relative',
        }}
      >
        {/* Wordmark header */}
        <div style={{
          padding: collapsed ? '20px 0 16px' : '20px 16px 16px',
          borderBottom: '1px solid #1f2937',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          transition: 'padding 0.25s ease',
        }}>
          <Wordmark collapsed={collapsed} />
          <button className="sidebar-close-btn" onClick={closeNav}>
            {Icons.close}
          </button>
        </div>

        {/* Nav links */}
        <nav style={{
          padding: collapsed ? '12px 6px' : '12px 8px',
          flex: 1,
          overflow: 'auto',
          transition: 'padding 0.25s ease',
        }}>
          <div style={{ marginBottom: '2px' }}>
            <NavItem to="/" end icon={Icons.dashboard} label="Dashboard" collapsed={collapsed} onClick={closeNav} />
          </div>

          {collapsed ? <div style={{ height: 12 }} /> : (
            <div style={{
              fontSize: '10px', fontWeight: '600', letterSpacing: '0.08em',
              textTransform: 'uppercase', color: '#374151', padding: '12px 12px 6px',
            }}>Operations</div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
            <NavItem to="/mandates"     icon={Icons.pipeline}     label="BD Pipeline"  collapsed={collapsed} onClick={closeNav} />
            <NavItem to="/contacts"     icon={Icons.contacts}     label="Contacts"     collapsed={collapsed} onClick={closeNav} />
            <NavItem to="/partners"     icon={Icons.partners}     label="Partners"     collapsed={collapsed} onClick={closeNav} />
            <NavItem to="/firms"        icon={Icons.firms}        label="Firms"        collapsed={collapsed} onClick={closeNav} />
            <NavItem to="/interactions" icon={Icons.interactions} label="Interactions" collapsed={collapsed} onClick={closeNav} />
          </div>

          {collapsed ? <div style={{ height: 12 }} /> : (
            <div style={{
              fontSize: '10px', fontWeight: '600', letterSpacing: '0.08em',
              textTransform: 'uppercase', color: '#374151', padding: '12px 12px 6px',
            }}>Account</div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
            <NavItem to="/settings" icon={Icons.settings} label="Settings" collapsed={collapsed} onClick={closeNav} />
          </div>
        </nav>

        {/* Footer */}
        <div style={{
          padding: collapsed ? '12px 6px' : '12px 16px',
          borderTop: '1px solid #1f2937',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          transition: 'padding 0.25s ease',
          alignItems: collapsed ? 'center' : 'stretch',
        }}>
          {!collapsed && user?.email && (
            <div style={{
              fontSize: '11px', color: '#9ca3af',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }} title={user.email}>
              {user.email}
            </div>
          )}
          <button
            onClick={signOut}
            title={collapsed ? 'Sign Out' : undefined}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start',
              gap: '8px', background: 'transparent', border: '1px solid #1f2937',
              borderRadius: '6px', padding: collapsed ? '7px' : '6px 10px',
              color: '#9ca3af', fontSize: '12px', cursor: 'pointer',
              fontFamily: 'inherit', transition: 'background 0.12s, color 0.12s, border-color 0.12s',
              width: collapsed ? '34px' : '100%',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.04)'
              e.currentTarget.style.color = '#e5e7eb'
              e.currentTarget.style.borderColor = '#2d3748'
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'transparent'
              e.currentTarget.style.color = '#9ca3af'
              e.currentTarget.style.borderColor = '#1f2937'
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            {!collapsed && 'Sign Out'}
          </button>
          {!collapsed && (
            <div style={{ fontSize: '11px', color: '#374151', letterSpacing: '0.02em' }}>
              v1.0 · Phase 1
            </div>
          )}
        </div>

        {/* ── Collapse toggle — desktop only ────────────────────────────
             Pill button anchored to the right edge, vertically centred.
             Hidden on mobile via CSS (.sidebar-collapse-btn).
        ──────────────────────────────────────────────────────────────── */}
        <button
          onClick={toggleCollapsed}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="sidebar-collapse-btn"
          style={{
            position: 'absolute',
            top: '50%',
            right: '-12px',
            transform: 'translateY(-50%)',
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            background: '#1f2937',
            border: '1px solid #374151',
            color: '#9ca3af',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            transition: 'background 0.12s, color 0.12s, border-color 0.12s',
            flexShrink: 0,
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = '#3b82f6'
            e.currentTarget.style.color = '#fff'
            e.currentTarget.style.borderColor = '#3b82f6'
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = '#1f2937'
            e.currentTarget.style.color = '#9ca3af'
            e.currentTarget.style.borderColor = '#374151'
          }}
        >
          {collapsed ? Icons.chevronRight : Icons.chevronLeft}
        </button>

      </aside>

      {/* ── Main content ─────────────────────────────────────────────── */}
      <main style={{ flex: 1, overflow: 'auto', background: '#0a0f1e' }}>
        {children}
      </main>

    </div>
  )
}
